import { ChevronDown, Star } from 'lucide-react';
import { NavLink } from '@/components/NavLink';
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from '@/components/ui/sidebar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';

export interface FavoritesSectionProps {
  favorites: string[];
  collapsed: boolean;
  sidebarCollapsed: boolean;
  onOpenChange: (open: boolean) => void;
  getTitle: (path: string) => string;
  isActive: (path: string) => boolean;
}

export function FavoritesSection({
  favorites,
  collapsed,
  sidebarCollapsed,
  onOpenChange,
  getTitle,
  isActive,
}: FavoritesSectionProps) {
  if (favorites.length === 0) return null;

  return (
    <SidebarGroup>
      <SidebarGroupLabel className="text-[10px] uppercase tracking-widest text-sidebar-muted px-2">
        {!sidebarCollapsed && 'Favoritos'}
      </SidebarGroupLabel>
      <SidebarGroupContent>
        <Collapsible open={!collapsed} onOpenChange={onOpenChange}>
          <CollapsibleTrigger
            className={cn(
              'flex w-full items-center justify-between rounded-md px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-sidebar-muted hover:bg-sidebar-accent transition-colors',
              sidebarCollapsed && 'justify-center',
            )}
            aria-label="Mostrar u ocultar favoritos"
          >
            {!sidebarCollapsed && <span className="flex items-center gap-1.5"><Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />Favoritos</span>}
            {!sidebarCollapsed && (
              <ChevronDown
                className={cn('h-3 w-3 transition-transform', collapsed && '-rotate-90')}
              />
            )}
            {sidebarCollapsed && <Star className="h-3.5 w-3.5 fill-yellow-400 text-yellow-400" />}
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenu>
              {favorites.map((path) => (
                <SidebarMenuItem key={path}>
                  <SidebarMenuButton asChild>
                    <NavLink
                      to={path}
                      className={cn(
                        'flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent hover:text-sidebar-foreground transition-colors',
                        isActive(path) && 'bg-sidebar-accent text-sidebar-primary font-medium',
                      )}
                      activeClassName="bg-sidebar-accent text-sidebar-primary font-medium"
                      title={getTitle(path)}
                    >
                      <Star className="h-4 w-4 shrink-0 fill-yellow-400 text-yellow-400" />
                      {!sidebarCollapsed && <span className="truncate">{getTitle(path)}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </CollapsibleContent>
        </Collapsible>
      </SidebarGroupContent>
    </SidebarGroup>
  );
}
